import { Box, Typography, Stack, InputAdornment } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import PeopleSearch from 'components/PeopleSearch'
import moment from 'moment'
import { isNilOrEmpty } from 'packages/core'
import { Icon } from 'packages/eid-icons'
import { WithCloseButton, Avatar, Tooltip, ToggleSwitch } from 'packages/eid-ui'
import { MUIDatePicker } from 'packages/eid-ui/DateTimePickers'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { getFullImageUrl } from 'utils'

const CheckOutGrid = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '1.8rem',
    marginBottom: '3.2rem',
    width: '100%',

    [theme.breakpoints.down('lg')]: {
        maxWidth: 'none',
    },

    '& .dateInputLabel': {
        color: '#b4b4b4',
        fontSize: '10px',
        textTransform: 'uppercase',
        width: '65px',
        position: 'absolute',
        left: '10px',
        top: '4px',
        zIndex: 50,
    },
    '& .dateMainWidthContainer': {
        gridColumn: 'span 3',

        [theme.breakpoints.down('lg')]: {
            gridColumn: 'span 6',
        },
    },
    '& .dateContainerLabel': {
        gridColumn: 'span 6',
    },
    '& .dueDateInputContainer': {
        gridColumn: 'span 3',
        [theme.breakpoints.down('lg')]: {
            gridColumn: 'span 6',
        },
        display: 'flex',
        alignItems: 'center',
        position: 'relative',

        '& svg': {
            marginTop: '-10px',
            marginRight: '4px',
        },
        '& input': {
            padding: '10px',
            paddingLeft: '80px',
            color: '#5d6870',
            fontSize: '16px',
            textAlign: 'center',
        },
        '& fieldset': {
            border: 'none',
        },
        '& > div:nth-child(1)': {
            width: '100%',
        },
        '&:hover': {
            cursor: 'pointer',
            '& input': {
                cursor: 'pointer',
            },
        },
        '@supports (-moz-appearance:none)': {
            '& input': {
                paddingLeft: '5.7rem',
            },
        },
    },
    '& .peopleSearchContainer': {
        gridColumn: 'span 3',
        [theme.breakpoints.down('lg')]: {
            gridColumn: 'span 5',
        },

        '& svg': {
            position: 'relative',
            left: '10px',
        },
    },
    '& .personIndicatorContainer': {
        gridColumn: 'span 3',
        [theme.breakpoints.down('lg')]: {
            gridColumn: 'span 5',
        },
    },
    '& .checkoutTypography': {
        paddingTop: '1rem',
        paddingRight: '1rem',
        fontSize: '1.4rem',
    },
    '& .checkOutToggle': {
        width: '100px',
        [theme.breakpoints.down('lg')]: {
            gridColumn: 'span 1',
        },
    },
}))

const tooltipStyles = {
    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
    padding: '8px',
    maxWidth: '800px', // some arbitrary width
}

const PersonIndicator = styled(WithCloseButton)<{ children?: React.ReactNode }>(
    ({ theme }) => ({
        borderRadius: '5px',
        backgroundColor: '#535b62',
        padding: '10px 22px 10px 16px',
        color: theme?.palette?.common?.white,
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '40px',
    }),
)

const MainLabel = styled('label')({
    lineHeight: 1.2,
    width: '25rem',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'block',
    color: ' #b4b4b4',
    textTransform: 'uppercase',
    marginBottom: '0.5rem',
})

export interface ComputerSessionRequestFiltersProps {
    isCheckedOut: boolean
    checkboxLabel: string
    dateContainerLabel?: string
    handleCheckedClick: (e: React.ChangeEvent<HTMLInputElement>) => void
    startDate: string
    endDate: string
    setStartDate: (e: any) => void
    setEndDate: (e: any) => void
    onPersonSelect: (e: any) => void
    selectedPerson: any
}

export const ComputerSessionRequestFilters: FC<
    ComputerSessionRequestFiltersProps
> = (props) => {
    const {
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        handleCheckedClick,
        isCheckedOut,
        checkboxLabel,
        dateContainerLabel,
        onPersonSelect,
        selectedPerson,
    } = props

    const theme = useTheme()
    const { t } = useTranslation()

    return (
        <CheckOutGrid>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    gap: '4rem',
                }}
            >
                {/* Checkbox for checkout */}
                <Box className="checkOutToggle">
                    <MainLabel>{t('CheckedOut')}</MainLabel>
                    <Stack direction="row">
                        <Typography className="checkoutTypography">
                            {t('Common_No')}
                        </Typography>
                        <ToggleSwitch
                            onChange={(e: any) => {
                                handleCheckedClick(e)
                            }}
                            value={isCheckedOut}
                            label={checkboxLabel}
                        />
                    </Stack>
                </Box>
                {/* Date range */}
                <Box>
                    <Box>
                        {dateContainerLabel && (
                            <MainLabel
                                className="dateContainerLabel"
                                sx={{ marginBottom: '0.5rem' }}
                            >
                                {dateContainerLabel}
                            </MainLabel>
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '2.5rem',
                                alignItems: 'center',
                            }}
                        >
                            <Box className="dateMainWidthContainer">
                                <MUIDatePicker
                                    dateFormat="L"
                                    value={startDate ? moment(startDate) : null}
                                    handleChange={(date: any) => {
                                        if (isNilOrEmpty(date)) {
                                            setStartDate(null)
                                        } else {
                                            setStartDate(
                                                moment(date)
                                                    .clone()
                                                    .local()
                                                    .format('YYYY-MM-DD'),
                                            )
                                        }
                                    }}
                                    //@ts-ignore
                                    maxDate={endDate ? moment(endDate) : null}
                                    customComponents={{
                                        inputAdornment: () => {
                                            return (
                                                <InputAdornment position="end">
                                                    <Icon
                                                        name="Tasks"
                                                        width={19}
                                                        height={20}
                                                        color="#959598"
                                                    />
                                                </InputAdornment>
                                            )
                                        },
                                    }}
                                    fieldProps={{
                                        variant: 'outlined',
                                        style: {
                                            backgroundColor:
                                                theme?.palette?.common?.white,
                                            width: '100%',
                                            boxShadow:
                                                '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
                                            borderRadius: '5px',
                                        },
                                        sx: {
                                            '& .MuiOutlinedInput-notchedOutline':
                                                {
                                                    borderColor: '#ebebed',
                                                },
                                        },
                                        placeholder: t('Common_SelectDate'),
                                        InputProps: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography
                                                        sx={{
                                                            fontSize:
                                                                '1rem !important',
                                                            textTransform:
                                                                'uppercase',
                                                            width: '6.5rem',
                                                            whiteSpace:
                                                                'normal',
                                                        }}
                                                    >
                                                        {t('Common_StartDate')}
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                height: '4rem !important',
                                                fontSize: '1.6rem !important',
                                            },
                                        },
                                        inputProps: {
                                            style: {
                                                color: '#5d6870',
                                                paddingLeft: '20px',
                                            },
                                            readOnly: 'true',
                                        },
                                    }}
                                ></MUIDatePicker>
                            </Box>
                            <Box className="dateMainWidthContainer">
                                {/* End Date picker */}
                                <MUIDatePicker
                                    dateFormat="L"
                                    value={endDate ? moment(endDate) : null}
                                    handleChange={(date: string) => {
                                        if (isNilOrEmpty(date)) {
                                            setEndDate(null)
                                        } else {
                                            setEndDate(
                                                moment(date)
                                                    .clone()
                                                    .local()
                                                    .format('YYYY-MM-DD'),
                                            )
                                        }
                                    }}
                                    //@ts-ignore
                                    minDate={
                                        startDate ? moment(startDate) : null
                                    }
                                    customComponents={{
                                        inputAdornment: () => {
                                            return (
                                                <>
                                                    <InputAdornment position="end">
                                                        <Icon
                                                            name="Tasks"
                                                            width={19}
                                                            height={20}
                                                            color="#959598"
                                                        />
                                                    </InputAdornment>
                                                </>
                                            )
                                        },
                                    }}
                                    fieldProps={{
                                        variant: 'outlined',
                                        style: {
                                            backgroundColor:
                                                theme?.palette?.common?.white,
                                            width: '100%',
                                            boxShadow:
                                                '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
                                            borderRadius: '5px',
                                        },
                                        sx: {
                                            '& .MuiOutlinedInput-notchedOutline':
                                                {
                                                    borderColor: '#ebebed',
                                                },
                                        },
                                        placeholder: t('Common_SelectDate'),
                                        InputProps: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <Typography
                                                        sx={{
                                                            fontSize:
                                                                '1rem !important',
                                                            textTransform:
                                                                'uppercase',
                                                            width: '6.5rem',
                                                            whiteSpace:
                                                                'normal',
                                                        }}
                                                    >
                                                        {t('Common_EndDate')}
                                                    </Typography>
                                                </InputAdornment>
                                            ),
                                            sx: {
                                                fontSize: '1.6rem !important',
                                                height: '4rem !important',
                                            },
                                        },
                                        inputProps: {
                                            style: {
                                                color: '#5d6870',
                                                paddingLeft: '20px',
                                            },
                                            readOnly: 'true',
                                        },
                                    }}
                                ></MUIDatePicker>
                            </Box>
                        </Box>
                    </Box>
                </Box>
                {/* Who Requested filter Start */}
                <Box>
                    {selectedPerson ? (
                        <Box className="personIndicatorContainer">
                            <MainLabel>{t('CheckedOutBy')}</MainLabel>
                            <PersonIndicator
                                // @ts-ignore
                                onClose={() => {
                                    onPersonSelect(null)
                                }}
                            >
                                <Avatar
                                    size="small"
                                    src={getFullImageUrl(
                                        selectedPerson.imageThumbUrl,
                                    )}
                                />
                                {/* @ts-ignore */}
                                <Tooltip
                                    color="#5D6870"
                                    fontColor={theme?.palette?.common?.white}
                                    enterDelay={500}
                                    enterNextDelay={500}
                                    tooltipStyles={tooltipStyles}
                                    title={selectedPerson.friendlyName}
                                >
                                    <Typography
                                        style={{
                                            marginLeft: '21px',
                                            wordBreak: 'break-all',
                                        }}
                                        display="block"
                                        variant="body1"
                                        noWrap={true}
                                    >
                                        {selectedPerson.friendlyName}
                                    </Typography>
                                </Tooltip>
                            </PersonIndicator>
                        </Box>
                    ) : (
                        <Box className="peopleSearchContainer">
                            <PeopleSearch
                                labelComponent={MainLabel}
                                label={t('CheckedOutBy')}
                                value={selectedPerson}
                                onChange={(_: any, value: any) => {
                                    if (value) {
                                        onPersonSelect({
                                            id: value.id,
                                            friendlyName: value.friendlyName,
                                            imageThumbUrl: value.imageThumbUrl,
                                        })
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </CheckOutGrid>
    )
}

export default ComputerSessionRequestFilters
